import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import TechnicianDashboard from "./pages/TechnicianDashboard";
import BecomeTechnician from "./pages/BecomeTechnician";
import MyAccount from "./pages/MyAccount";
import Landing from "./pages/Landing";
import Legal from "./pages/Legal";
import Privacy from "./pages/Privacy";
import Contact from "./pages/Contact";
import PaymentMethods from "./pages/PaymentMethods";
import ClientAccount from "./pages/ClientAccount";
import "./App.css";
import TechnicianPayout from "./pages/TechnicianPayout";
import Survey from "./pages/Survey";
import Interventions from "./pages/Interventions";

function getRole() {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    return jwtDecode(token).role;
  } catch {
    return null;
  }
}

function useAuth() {
  const token = localStorage.getItem("token");
  const isLoggedIn = !!token;
  const role = isLoggedIn ? getRole() : null;

  const login = (token) => {
    localStorage.setItem("token", token);
    window.location.href = "/";
  };

  const logout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  return { isLoggedIn, role, login, logout };
}

// Redirige vers login si pas connecté
function PrivateRoute({ children }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/login" />;
}

// Redirige vers dashboard si déjà connecté
function PublicOnly({ children }) {
  const { isLoggedIn } = useAuth();
  return !isLoggedIn ? children : <Navigate to="/" />;
}

// Composant Home — routing par rôle
function Home() {
  const { role, logout } = useAuth();
  const navigate = useNavigate();
  const onNavigate = (p) => navigateTo(navigate, p);

  if (role === "admin") return <AdminDashboard onLogout={logout} onNavigate={onNavigate} />;
  if (role === "technicien") return <TechnicianDashboard onLogout={logout} onNavigate={onNavigate} />;
  return <Dashboard onLogout={logout} onNavigate={onNavigate} />;
}

// Scroll to top à chaque changement de route
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Gère le callback OAuth
function OAuthSuccess() {
  const { login } = useAuth();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) login(token);
    else window.location.href = "/";
  }, []);
  return null;
}

export default function App() {
  const { isLoggedIn, role, logout } = useAuth();
  const navigateAdapter = (navigate) => (p) => {
    const routes = {
      "home": "/",
      "login": "/login",
      "legal": "/mentions-legales",
      "privacy": "/confidentialite",
      "contact": "/contact",
      "become-technician": "/devenir-technicien",
      "my-account": "/mon-compte",
      "payment-methods": "/paiements",
      "contact-faq-payment": "/contact?faq=5",
      "technician-payout": "/technicien-paiements",
      "interventions": "/interventions",
    };
    navigate(routes[p] || `/${p}`);
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* OAuth callback */}
        <Route path="/oauth-success" element={<OAuthSuccess />} />

        {/* Pages publiques */}
        <Route path="/mentions-legales" element={
          <WrapPublic component={Legal} isLoggedIn={isLoggedIn} onLogout={logout} />
        } />
        <Route path="/confidentialite" element={
          <WrapPublic component={Privacy} isLoggedIn={isLoggedIn} onLogout={logout} />
        } />
        <Route path="/contact" element={
          <WrapPublic component={Contact} isLoggedIn={isLoggedIn} onLogout={logout} />
        } />
        <Route path="/sondage" element={
          <WrapPublic component={Survey} isLoggedIn={isLoggedIn} onLogout={logout} />
        } />

        {/* Login */}
        <Route path="/login" element={
          <PublicOnly><WrapLogin /></PublicOnly>
        } />

        {/* Landing ou Dashboard */}
        <Route path="/" element={
          isLoggedIn ? <Home /> : <WrapLanding />
        } />

        {/* Pages privées */}
        <Route path="/devenir-technicien" element={
          <PrivateRoute><WrapBecomeTechnician logout={logout} /></PrivateRoute>
        } />
        <Route path="/mon-compte" element={
          <PrivateRoute><WrapMyAccount role={role} logout={logout} /></PrivateRoute>
        } />
        <Route path="/paiements" element={
          <PrivateRoute><WrapPaymentMethods logout={logout} /></PrivateRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" />} />

        <Route path="/technicien-paiements" element={
          <PrivateRoute><WrapTechnicianPayout logout={logout} /></PrivateRoute>
        } />
        <Route path="/interventions" element={
          <PrivateRoute><WrapInterventions logout={logout} /></PrivateRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}

// --- Wrappers pour adapter onNavigate vers useNavigate ---

function WrapPublic({ component: Component, isLoggedIn, onLogout }) {
  const navigate = useNavigate();
  const onNavigate = (p) => navigateTo(navigate, p);
  return <Component onNavigate={onNavigate} isLoggedIn={isLoggedIn} onLogout={onLogout} />;
}

function WrapLanding() {
  const navigate = useNavigate();
  return <Landing onLogin={() => navigate("/login")} onNavigate={(p) => navigateTo(navigate, p)} />;
}

function WrapLogin() {
  const navigate = useNavigate();
  const handleLogin = () => {
    // Le token est déjà dans localStorage via Login.js
    navigate("/");
    window.location.reload();
  };
  return <Login onLogin={handleLogin} onNavigate={(p) => navigateTo(navigate, p)} />;
}

function WrapBecomeTechnician({ logout }) {
  const navigate = useNavigate();
  return <BecomeTechnician onLogout={logout} onBack={() => navigate("/")} onSuccess={() => navigate("/")} />;
}

function WrapMyAccount({ role, logout }) {
  const navigate = useNavigate();
  const onNavigate = (p) => navigateTo(navigate, p);
  if (role === "technicien") return <MyAccount onLogout={logout} onNavigate={onNavigate} />;
  return <ClientAccount onLogout={logout} onNavigate={onNavigate} />;
}

function WrapPaymentMethods({ logout }) {
  const navigate = useNavigate();
  return <PaymentMethods onLogout={logout} onNavigate={(p) => navigateTo(navigate, p)} />;
}

function WrapTechnicianPayout({ logout }) {
  const navigate = useNavigate();
  return <TechnicianPayout onLogout={logout} onNavigate={(p) => navigateTo(navigate, p)} />;
}

function WrapInterventions({ logout }) {
  const navigate = useNavigate();
  return <Interventions onLogout={logout} onNavigate={(p) => navigateTo(navigate, p)} />;
}

// --- Helper central de navigation ---
function navigateTo(navigate, page) {
  const routes = {
    "home": "/",
    "login": "/login",
    "legal": "/mentions-legales",
    "privacy": "/confidentialite",
    "contact": "/contact",
    "become-technician": "/devenir-technicien",
    "my-account": "/mon-compte",
    "payment-methods": "/paiements",
    "contact-faq-payment": "/contact?faq=5",
    "technician-payout": "/technicien-paiements",
    "survey": "/sondage",
  };
  navigate(routes[page] || `/${page}`);
}